import { type ReactNode, createContext, useContext } from 'react';

import { type GridColDef } from '@mui/x-data-grid-premium';

import type {
  ActivateListingImageVersion,
  SaveListingImageVersion,
} from '@modules/_shared/components/image-gallery/enhance/enhance-image-types';
import type { ImageGalleryItem } from '@modules/_shared/components/image-gallery/NewImageGallery';

export type EditableImage = {
  id: string;
  order_nr: number;
  image: {
    id: string;
    url?: string | null;
  };
};

export type ViewMode = 'list' | 'grid';

export type EditableImagesContextValue<
  TImage extends ImageGalleryItem,
  TFormData extends Record<string, any> = Record<string, any>,
> = {
  // Config values
  readOnly: boolean;
  canEditMeta: boolean;
  metaColumns: GridColDef<TImage>[];
  autoSave: boolean;
  enableViewMode: boolean;
  autoSelectFirst: boolean;

  // Data values
  images: TImage[];
  loading: boolean;
  loadingMetadataIds?: Set<string>;
  deleteImage: (id: string) => Promise<void>;
  updateImage: (id: string, data: TFormData) => Promise<boolean>;
  uploadImages: (uploadedFiles: { id: string; url: string }[]) => Promise<void>;
  reorderImage: (id: string, targetPosition: number) => Promise<void>;
  generateMetadataForImage?: (imageId: string) => Promise<unknown>;
  regenerateAllMetadata?: () => Promise<void>;
  regeneratingAll?: boolean;
  enhanceImage?: (
    prompt: string,
    sourceImageData: string,
  ) => Promise<{ image_data: string }>;
  saveImageVersion?: SaveListingImageVersion;
  activateImageVersion?: ActivateListingImageVersion;

  // UI values
  viewMode: ViewMode;
  setViewMode: (mode: ViewMode) => void;
  snackbarMessage: string | null;
  hideSnackbar: () => void;
};

export const EditableImagesContext = createContext<EditableImagesContextValue<
  any,
  any
> | null>(null);

export const useEditableImagesContext = <
  TImage extends ImageGalleryItem,
  TFormData extends Record<string, any> = Record<string, any>,
>() => {
  const context = useContext<EditableImagesContextValue<
    TImage,
    TFormData
  > | null>(EditableImagesContext);
  if (!context) {
    throw new Error(
      'useEditableImagesContext must be used within an EditableImagesProvider',
    );
  }
  return context;
};

export type EditableImagesProviderProps<TImage extends ImageGalleryItem> = {
  children: ReactNode;
  canEditMeta?: boolean;
  metaColumns?: GridColDef<TImage>[];
  autoSave?: boolean;
  enableViewMode?: boolean;
  autoSelectFirst?: boolean;
  initialViewMode?: ViewMode;
};

export const getOptimisticReorderResponse = <TImage extends EditableImage>(
  pImages: TImage[],
  id: string,
  targetPosition: number,
) => {
  const images = [...pImages];
  const draggedIndex = images.findIndex(img => img.id === id);

  if (draggedIndex === -1) {
    return null;
  }

  const safeTargetPosition = Math.min(targetPosition, images.length - 1);
  const targetOrderNr = images[safeTargetPosition].order_nr;

  const reorderedImages = images
    .map((img, index) => {
      let newOrderNr = img.order_nr;

      if (targetPosition < draggedIndex) {
        // Dragging up: increase order_nr for images between target and start
        if (index >= targetPosition && index < draggedIndex) {
          newOrderNr = img.order_nr + 1;
        }
      } else if (targetPosition > draggedIndex) {
        // Dragging down: decrease order_nr for images between start and target
        if (index > draggedIndex && index <= targetPosition) {
          newOrderNr = img.order_nr - 1;
        }
      }

      // The dragged image gets the target position's order_nr
      if (img.id === id) {
        newOrderNr = targetOrderNr;
      }

      return {
        ...img,
        order_nr: newOrderNr,
      };
    })
    .sort((a, b) => a.order_nr - b.order_nr);

  return reorderedImages;
};
